import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, API } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { OrderCard } from "../../components/vc/OrderCard";
import { EmptyState, Section, SkeletonList } from "../../components/vc/layout";
import { SearchBar } from "../../components/vc/SearchBar";
import { ShoppingBag, Plus, Sparkles, ArrowUpRight, Download, Inbox, Layers, ChevronDown } from "lucide-react";
import { useNotifications, polarityOf } from "../../context/NotificationContext";

const OUTBOUND_STATUS = {
  placed: "pending",
  in_progress: "in_progress",
  delivered: "delivered",
  cancelled: "closed",
};
const INBOUND_STATUS = OUTBOUND_STATUS;

export default function VCOrdersDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("incoming"); // incoming | outgoing | completed
  const [q, setQ] = useState("");
  const [outbound, setOutbound] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [loading, setLoading] = useState(true);
  const { counts: notifCounts, list: notifList } = useNotifications();
  const ordersCount = notifCounts.by_section.orders;
  const prevOrdersCount = useRef(ordersCount);

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    Promise.all([
      apiClient.get("/orders").then((r) => r.data).catch(() => []),
      apiClient.get("/inbound-orders").then((r) => r.data).catch(() => []),
    ]).then(([o, i]) => {
      setOutbound(o || []);
      setInbound(i || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // #2 — Incoming Order auto-update: the shared notification poll bumps
  // counts.by_section.orders as soon as a new order arrives; react to that
  // change here instead of running a second, independent poll.
  useEffect(() => {
    if (ordersCount !== prevOrdersCount.current) {
      prevOrdersCount.current = ordersCount;
      load(false);
    }
  }, [ordersCount]);

  const toCard = (x, incoming = false) => ({
    id: x.id,
    business_name: incoming ? (x.buyer_name || "Buyer") : (x.supplier_name || "Business"),
    order_id_label: (x.id || "").slice(0, 6).toUpperCase(),
    enquiry_title: x.enquiry_title || "",
    price_request_id: x.price_request_id || null,
    incoming,
    to: incoming ? `/inbound-orders/${x.id}` : `/orders/${x.id}`,
    date: x.created_at,
    amount: x.price,
    status: (INBOUND_STATUS)[x.status] || "pending",
    items_count: x.lines?.length,
  });

  // Order History Grouping — a trader's own outgoing orders that came from the
  // same enquiry (shared price_request_id) are bundled under one collapsible
  // group so they can see everything they ordered off that enquiry together.
  // Only applies when 2+ orders share an enquiry (a lone order already shows
  // its enquiry_title inline on the card — no need for a group wrapper).
  // Incoming orders never group: enquiry_title is scrubbed for non-owners.
  const groupByEnquiry = (cards) => {
    const bySrc = new Map();
    for (const c of cards) {
      if (!c.incoming && c.price_request_id) {
        const arr = bySrc.get(c.price_request_id) || [];
        arr.push(c);
        bySrc.set(c.price_request_id, arr);
      }
    }
    const multi = new Set(
      Array.from(bySrc.entries()).filter(([, arr]) => arr.length > 1).map(([id]) => id)
    );
    const entries = [];
    const seen = new Set();
    for (const c of cards) {
      if (c.price_request_id && multi.has(c.price_request_id)) {
        if (seen.has(c.price_request_id)) continue;
        seen.add(c.price_request_id);
        const groupCards = bySrc.get(c.price_request_id)
          .slice()
          .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        entries.push({
          type: "group",
          key: c.price_request_id,
          title: groupCards.find((g) => g.enquiry_title)?.enquiry_title || "Enquiry",
          cards: groupCards,
          date: groupCards[0].date,
          total: groupCards.reduce((s, g) => s + (g.amount || 0), 0),
        });
      } else {
        entries.push({ type: "single", card: c, date: c.date });
      }
    }
    entries.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return entries;
  };

  const isCompleted = (o) => ["delivered", "cancelled"].includes(o.status);
  const isPending = (o) => ["placed", "in_progress"].includes(o.status);

  const list = useMemo(() => {
    let src = [];
    if (tab === "incoming") {
      src = inbound.filter(isPending).map((o) => toCard(o, true));
    } else if (tab === "outgoing") {
      src = outbound.filter(isPending).map((o) => toCard(o));
    } else {
      // completed: from both sides
      src = [
        ...outbound.filter(isCompleted).map((o) => toCard(o)),
        ...inbound.filter(isCompleted).map((o) => toCard(o, true)),
      ];
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      src = src.filter((c) =>
        (c.business_name || "").toLowerCase().includes(needle) ||
        (c.order_id_label || "").toLowerCase().includes(needle)
      );
    }
    // Sort newest first
    src.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return src;
  }, [tab, q, outbound, inbound]);

  const counts = {
    incoming: inbound.filter(isPending).length,
    outgoing: outbound.filter(isPending).length,
    completed: outbound.filter(isCompleted).length + inbound.filter(isCompleted).length,
  };

  // Sub-tab (Incoming/Outgoing/Completed) badge color — green if any unread
  // positive event touches an order in that bucket, red if any negative one
  // does (red wins). Pure client-side lookup against the already-polled
  // notification list, keyed by order id (entity_id) — no extra request.
  const unreadOrderPolarity = useMemo(() => {
    const m = new Map();
    for (const n of notifList) {
      if (n.read || !n.entity_id) continue;
      if (polarityOf(n.type) === "negative") m.set(n.entity_id, "negative");
      else if (!m.has(n.entity_id)) m.set(n.entity_id, "positive");
    }
    return m;
  }, [notifList]);
  const tabDotColor = (orders) => {
    let any = false;
    for (const o of orders) {
      const p = unreadOrderPolarity.get(o.id);
      if (p === "negative") return "bg-red-500";
      if (p === "positive") any = true;
    }
    return any ? "bg-emerald-500" : null;
  };
  const tabDots = {
    incoming: tabDotColor(inbound.filter(isPending)),
    outgoing: tabDotColor(outbound.filter(isPending)),
    completed: tabDotColor([...outbound.filter(isCompleted), ...inbound.filter(isCompleted)]),
  };

  const entries = useMemo(() => groupByEnquiry(list), [list]);

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-28 space-y-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Orders</h2>
          <p className="text-sm text-slate-500 mt-0.5">Manage buying and selling</p>
        </div>

        {/* Hero — Generate Order */}
        <button
          onClick={() => navigate("/orders/generate")}
          data-testid="generate-order-hero"
          className="w-full text-left relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white p-5 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 transition-all group"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -right-4 bottom-0 w-24 h-24 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur grid place-items-center shrink-0">
              <Plus className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-90">
                <Sparkles className="w-3 h-3" /> Start
              </div>
              <div className="text-lg font-bold leading-tight mt-1">Generate Order</div>
              <div className="text-xs opacity-80 mt-0.5">From quotation · Excel · Image · Manual</div>
            </div>
            <ArrowUpRight className="w-6 h-6 opacity-80 group-hover:rotate-45 transition-transform" />
          </div>
        </button>

        {/* Tabs — Incoming / Outgoing / Completed */}
        <div className="flex bg-slate-100 rounded-2xl p-1" data-testid="orders-tabs">
          {[
            ["incoming", "Incoming", counts.incoming],
            ["outgoing", "Outgoing", counts.outgoing],
            ["completed", "Completed", counts.completed],
          ].map(([v, l, c]) => (
            <button
              key={v}
              data-testid={`orders-tab-${v}`}
              onClick={() => setTab(v)}
              className={`flex-1 h-9 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 ${
                tab === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
            >
              {tabDots[v] && (
                <span data-testid={`orders-tab-${v}-dot`} className={`w-1.5 h-1.5 rounded-full ${tabDots[v]}`} />
              )}
              {l} <span className="opacity-60">({c})</span>
            </button>
          ))}
        </div>

        {/* Search + Excel export */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <SearchBar value={q} onChange={setQ} placeholder="Search orders" testid="orders-search" />
          </div>
          <a
            href={`${API}/exports/orders.xlsx`}
            target="_blank"
            rel="noreferrer"
            data-testid="orders-export"
            className="w-11 h-11 rounded-2xl bg-slate-50 hover:bg-slate-100 grid place-items-center text-slate-600 transition"
            aria-label="Export"
          >
            <Download className="w-4 h-4" strokeWidth={2.4} />
          </a>
        </div>

        {/* Order list */}
        <Section
          title={tab === "incoming" ? "Incoming Orders" : tab === "outgoing" ? "Outgoing Orders" : "Completed Orders"}
          subtitle={`${list.length} shown`}
          testid="orders-list-section"
        >
          {loading ? <SkeletonList count={4} height="h-28" /> : list.length === 0 ? (
            <EmptyState
              Icon={ShoppingBag}
              title={q ? "No matches" : tab === "incoming" ? "No incoming orders yet" : tab === "outgoing" ? "No outgoing orders yet" : "No completed orders yet"}
              subtitle={q ? "Try different keywords" : "Tap Generate Order to create one"}
            />
          ) : (
            <div className="space-y-2">
              {entries.map((e) =>
                e.type === "group" ? (
                  <EnquiryOrderGroup key={e.key} group={e} />
                ) : (
                  <OrderCard
                    key={e.card.id}
                    order={e.card}
                    to={e.card.to}
                    testid={`orders-card-${e.card.id}`}
                  />
                )
              )}
            </div>
          )}
        </Section>
      </div>
    </>
  );
}


function EnquiryOrderGroup({ group }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`orders-group-${group.key}`}
      className="bg-indigo-50/60 border border-indigo-100 rounded-2xl overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`orders-group-toggle-${group.key}`}
        className="w-full flex items-center gap-3 p-4 text-left active:scale-[0.99] transition"
      >
        <div className="w-11 h-11 rounded-2xl bg-indigo-100 text-indigo-600 grid place-items-center shrink-0">
          <Layers className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 truncate">{group.title}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {group.cards.length} orders · ₹{group.total.toLocaleString("en-IN")}
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-indigo-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid={`orders-group-body-${group.key}`}>
          {group.cards.map((c) => (
            <OrderCard key={c.id} order={c} to={c.to} testid={`orders-card-${c.id}`} />
          ))}
        </div>
      )}
    </div>
  );
}
